import { cloneDeep, isArray, isEmpty, isEqual, mapValues, omit, pick, uniqBy } from "lodash";
import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import { isProfileActive } from "PFCore/helpers/profile";
import { isUserResourcer } from "PFCore/helpers/templates";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useBookingsFromAvailabilityInvalidate } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability_invalidate";
import { usePhasesInvalidate } from "PFCore/hooks/queries/phases/use_phases";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { createActivity } from "PFCore/services/activities";
import { deleteAssignee, updateAssignee } from "PFCore/services/assignees";
import {
  Activity,
  ActivityProfile,
  Attachment,
  BookingCategory,
  CustomField,
  Metadata,
  Shortlist,
  Subtemplate,
  Template,
  TemplateConfiguration
} from "PFTypes";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { getPostUntilChangeReason, getPostUntilChangeReasonData } from "./sections/expiry_date_section/utils";
import { useEngagementWizard } from "./use_engagement_wizard";
import {
  getInitialQuestionsToggleValue,
  getInitialQuestionValue,
  getPayloadCustomFields,
  getQuestionsTileVisibility,
  isSubtemplatesLogicActive,
  WORKFLOW_STATUSES_WITH_BOOKINGS
} from "./utils/activity_edit_form.utils";
import useConfirmModalsManager, { ModalKey } from "./utils/use_confirm_modals_manager";
import { useSkillsFrameworkLink } from "./utils/use_skills_framework_link";
import { useSubtemplates } from "./utils/use_subtemplates";

type UseActivityEditFormProps = {
  template: Template;
  activity: Activity;
  parentActivity: Activity;
  activityAttachments: Attachment[];
  isClone: boolean;
  isEdit: boolean;
  isForcePrivate: boolean;
  isForceVisibilitySet: boolean;
  templateConfig: TemplateConfiguration;
  subtemplates: Subtemplate[];
  requireAssignee: boolean;
  shortlistsToCopy: Shortlist[];
};

const checkIfIsDefaultPrivate = (template: Template, parentActivity: Activity) => {
  if (parentActivity && template.default_visibility === "inherit_from_parent") {
    return parentActivity.private;
  } else {
    return template.default_visibility === "private";
  }
};

export const useActivityEditForm = ({
  template,
  activity,
  parentActivity,
  activityAttachments,
  isClone,
  isEdit,
  isForcePrivate,
  isForceVisibilitySet,
  templateConfig,
  subtemplates,
  requireAssignee,
  shortlistsToCopy
}: UseActivityEditFormProps) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation("activities");
  const history = useHistory();
  const { update: updateActivity } = useActivityUpdate(activity.id);
  const { mutateAsync: createShortlist } = useShortlistCreate({}, false);
  const { invalidate: invalidateBookingsFromAvailability } = useBookingsFromAvailabilityInvalidate();
  const { invalidate: invlidateAcitivityPhases } = usePhasesInvalidate();
  const { invalidateAuditRoles, invalidateRoles } = useRolesInvalidate();
  const { getFormattedErrors } = useResponseErrors();
  const { bookingCategories } = useBookingCategories();

  const growl = useGrowl();

  const defaultName = isClone
    ? t("edit.sections.clone", { activityName: activity.name })
    : activity.name || "";
  const isDefaultPrivate = isForceVisibilitySet
    ? isForcePrivate
    : checkIfIsDefaultPrivate(template, parentActivity);

  const isAudit = template.key.includes("audit");
  const isNew = !isEdit;
  const isDraft = activity.state === "draft";
  const isEngagement = ["engagement", "audit_engagement"].includes(template.key);
  const isRole = ["role", "audit_role"].includes(template.key);
  const doAutofillAssignee = requireAssignee && isUserResourcer(currentProfile.permissions_group);

  const [name, setName] = useState<string>(defaultName);
  const [description, setDescription] = useState<string>(activity.description || "");
  const [postUntil, setPostUntil] = useState<string | undefined>(activity.post_until);
  const [selectedParentActivity, setSelectedParentActivity] = useState<Activity | undefined>(parentActivity);
  const reason = getPostUntilChangeReason(
    activity.post_until_change_reason ?? null,
    template.localized_post_until_change_reasons ?? []
  );
  const [postUntilChangeReason, setPostUntilChangeReason] = useState(reason);
  const [metadata, setMetadata] = useState<Metadata>(activity.metadata || {});
  const [customFields, setCustomFields] = useState<CustomField[]>(activity.custom_fields);
  const [assignee, setAssignee] = useState<ActivityProfile["profile"] | null>(
    isProfileActive(activity.assignee?.profile) ? activity?.assignee?.profile || null : null
  );
  const [attachments, setAttachments] = useState<Attachment[]>(activityAttachments);
  const [submitOptions, setSubmitOptions] = useState<{ draft?: boolean }>({});
  const [minRequiredCount, setMinRequiredCount] = useState<number>(activity.min_required_count || 1);
  const [isPrivate, setIsPrivate] = useState<boolean>(
    isClone || isEdit ? !!activity.private : isDefaultPrivate
  );
  const [audiences, setAudiences] = useState<string[][]>(activity.audiences || []);
  const [bookingCategory, setBookingCategory] = useState<BookingCategory | null>(
    bookingCategories.find(({ id }) => id === activity.booking_category_id) || null
  );
  const [communicationType, setCommunicationType] = useState<Activity["communication_type"]>(
    activity.communication_type || "direct"
  );

  const [errors, setErrors] = useState<{ interestQuestions?: string }>({});
  const [locked, setLocked] = useState<boolean>(false);

  const removeErrorIfPresent = useCallback(
    (fieldName) => {
      if (errors[fieldName]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const renderGrowlError = (message: string) =>
    growl({
      message,
      kind: "error"
    });

  const {
    subtemplateOptions,
    hasSubtemplates,
    subtemplate,
    subtemplateKey,
    isLoading,
    handleSubtemplateChange
  } = useSubtemplates({
    templateKey: template.key,
    subtemplates,
    activity,
    customFields,
    setCustomFields,
    metadata,
    setMetadata,
    removeErrorIfPresent,
    populateDefaultsEnabled: isNew && !isClone
  });

  const { skillsFramework, setSkillsFramework, showSkillsFrameworkButton } = useSkillsFrameworkLink({
    activity,
    template,
    subtemplate,
    customFields,
    setCustomFields,
    setMetadata,
    isEdit,
    isClone
  });

  const { isModalDisplayed, isModalConfirmed, setModalDisplayed, setModalConfirmed } =
    useConfirmModalsManager();

  const { engagementWizardState, handleEngagementWizardNavigation } = useEngagementWizard(
    activity,
    parentActivity,
    template
  );

  const initialQuestionsToggleValue = useMemo(
    () => getInitialQuestionsToggleValue(activity, templateConfig, isEdit, isClone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [questionsToggleValue, setQuestionsToggleValue] = useState(initialQuestionsToggleValue);

  const initialQuestionsValues = useMemo(
    () =>
      [1, 2, 3].map((id) => ({
        id,
        value: getInitialQuestionValue(id, activity, templateConfig, isEdit, isClone)
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const questionsRef = useRef(cloneDeep(initialQuestionsValues));

  const isAtLeastOneQuestion = useMemo(
    () => questionsRef.current.some(({ value }) => value.trim() !== ""),
    []
  );

  const showQuestionsTile = useMemo(
    () => getQuestionsTileVisibility(activity, templateConfig, isEdit, isClone, isAtLeastOneQuestion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleUploadRemove = (removedAttachment) =>
    setAttachments(attachments.filter((attachment) => attachment.id !== removedAttachment.id));

  const formData = () => {
    const interestQuestionnaireData: {
      interest_statement_enabled?: boolean;
      interest_questions?: string[];
    } = {};
    if (isNew) {
      interestQuestionnaireData.interest_statement_enabled = !!templateConfig?.show_statement;
      if (questionsToggleValue) {
        interestQuestionnaireData.interest_questions = questionsRef.current
          .map(({ value }) => value.trim())
          .filter((value) => !!value);
      }
    }

    const metadataToSave = {
      ...omit(metadata, ["interest_questions", "interest_statement_enabled"]),
      ...(isEmpty(metadata.availability?.ranges) && { availability: {} })
    };

    return {
      template_id: template.id,
      ...(subtemplateKey ? { subtemplate_key: subtemplateKey } : {}),
      post_until: postUntil,
      post_until_change_reason: getPostUntilChangeReasonData(postUntilChangeReason),
      custom_fields: getPayloadCustomFields(customFields),
      skills_framework_id: skillsFramework?.id || null,
      booking_category_id: bookingCategory?.id ?? null,
      attachment_ids: attachments.map((attachment) => attachment.id),
      communication_type: communicationType,
      private: isPrivate,
      metadata: metadataToSave,
      name,
      description,
      parent_activity_id: selectedParentActivity?.id,
      min_required_count: minRequiredCount,
      audiences: audiences.map((audience) => omit(audience, "unconfirmed")),
      ...interestQuestionnaireData
    };
  };

  const handleSubmit = (options: { draft?: boolean } = {}) => {
    let hasErrors = false;
    if (requireAssignee && !assignee) {
      setErrors((prev) => ({ ...prev, assignee: t("edit.cantBeBlank") }));
      hasErrors = true;
    }
    const isExpired = isDraft && postUntil && new Date(postUntil) < new Date();
    if (isExpired) {
      setErrors((prev) => ({ ...prev, post_until: t("edit.shouldNotBeExpired") }));
      hasErrors = true;
    }
    if (!subtemplate && isSubtemplatesLogicActive(hasSubtemplates, !!subtemplateKey, isEdit, isClone)) {
      setErrors((prev) => ({ ...prev, roleForm: t("edit.cantBeBlank") }));
      hasErrors = true;
    }

    if (isNew && questionsToggleValue && questionsRef.current.every(({ value }) => !value.trim())) {
      setErrors((prev) => ({ ...prev, interestQuestions: t("edit.interestQuestionsError") }));
      hasErrors = true;
    }

    if (hasErrors) {
      renderGrowlError(isNew ? t("edit.errorActivityNotCreated") : t("edit.errorActivityNotUpdated"));
      return null;
    }

    setLocked(true);
    const statePayload = (): { state?: Activity["state"] } => {
      if (activity.state === "complete") {
        return {};
      }
      return { state: options.draft ? "draft" : "new" };
    };

    const data = formData();

    if (
      !isEngagement &&
      !options.draft &&
      (isEmpty(metadata) ||
        (template.key === "role" && isEmpty(metadata.filters) && isEmpty(metadata.availability))) &&
      data.custom_fields.every((field) => isEmpty(field.values)) &&
      !isModalConfirmed[ModalKey.NoRequirements]
    ) {
      setModalDisplayed(ModalKey.NoRequirements, true);
      setSubmitOptions(options);
      setLocked(false);
      return;
    }

    if (
      isNew &&
      (questionsToggleValue !== initialQuestionsToggleValue ||
        !isEqual(initialQuestionsValues, questionsRef.current)) &&
      !isModalConfirmed[ModalKey.QuestionsChanged]
    ) {
      setModalDisplayed(ModalKey.QuestionsChanged, true);
      setSubmitOptions(options);
      setLocked(false);
      return;
    }

    const isAvailabilityChanged = () => {
      const dataAvailability = {
        ...(data.metadata.availability || {}),
        availability_threshold: undefined
      };
      const activityAvailability = {
        ...(activity.metadata.availability || {}),
        availability_threshold: undefined
      };
      return !isEqual(dataAvailability, activityAvailability);
    };

    if (
      isEdit &&
      isAvailabilityChanged() &&
      WORKFLOW_STATUSES_WITH_BOOKINGS.includes(activity.workflow_state) &&
      !isModalConfirmed[ModalKey.Misalignment]
    ) {
      setModalDisplayed(ModalKey.Misalignment, true);
      setSubmitOptions(options);
      setLocked(false);
      return;
    }

    const apiCallFunction = isNew ? createActivity : updateActivity;
    apiCallFunction({
      ...formData(),
      ...statePayload()
    }).then(
      (activity) => {
        // TODO: [ENG-2544][ENG-2606] this is not atomic, and n+1, it would be much better if we could
        // just pass `copy_shortlist_items_from_activity_id` with the save payload,
        // discuss with api if this is possible
        const promises = (shortlistsToCopy || []).map((shortlist) =>
          createShortlist({ activityId: activity.id, profileId: shortlist.profile.id })
        );

        const getAssigneePromise = () => {
          const shouldDelete = activity.assignee && !assignee;

          const catchFunction = () => {
            renderGrowlError(
              activity.id ? t("edit.errorActivityNotCreated") : t("edit.errorActivityNotUpdated")
            );
            setLocked(false);
          };
          if (assignee?.id !== activity.assignee?.id) {
            if (shouldDelete) {
              return deleteAssignee(activity.assignee.id).catch(catchFunction);
            }
            if (assignee) {
              return updateAssignee({
                profileId: assignee?.id,
                activityId: activity.id
              }).catch(catchFunction);
            }
          }

          return null;
        };

        Promise.all([...promises, getAssigneePromise()]).then(() => {
          if (handleEngagementWizardNavigation) {
            handleEngagementWizardNavigation(activity, options);
          } else {
            history.push(
              isAudit
                ? `/audit_planner/control_centre/${selectedParentActivity?.id}`
                : `/activities/${activity.id}/matches`
            );
          }
          setLocked(false);
        });
        invalidateBookingsFromAvailability(activity.id);
        invlidateAcitivityPhases(activity.id);
        if (isRole) {
          (isAudit ? invalidateAuditRoles : invalidateRoles)();
        }
      },
      ({ response }) => {
        setLocked(false);
        renderGrowlError(isNew ? t("edit.errorActivityNotCreated") : t("edit.errorActivityNotUpdated"));
        const errors = mapValues(getFormattedErrors(response), (val) =>
          isArray(val) ? val.join(", ") : val
        );
        setErrors(errors);
      }
    );
    setModalConfirmed(ModalKey.NoRequirements, false);
    setModalConfirmed(ModalKey.QuestionsChanged, false);
    setModalConfirmed(ModalKey.Misalignment, false);
  };

  const handleQuestionChange = useCallback(
    (id, newValue) => {
      removeErrorIfPresent("interestQuestions");
      const questions = questionsRef.current;
      const changedQuestionIndex = questions.findIndex((el) => el.id === id);
      const newQuestions = [...questions];
      newQuestions[changedQuestionIndex].value = newValue;
      questionsRef.current = newQuestions;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors.interestQuestions, questionsRef]
  );

  const handleQuestionsToggleChange = useCallback(
    (newValue: boolean) => {
      removeErrorIfPresent("interestQuestions");
      setQuestionsToggleValue(newValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors.interestQuestions]
  );

  const handlePrivateChange = useCallback(() => {
    if (!!activity.private && !isModalConfirmed[ModalKey.Public]) {
      setModalDisplayed(ModalKey.Public, true);
    } else {
      if (isPrivate) {
        setAudiences([]);
      } else {
        setAudiences(activity.audiences || []);
      }
      setIsPrivate(!isPrivate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRole, isPrivate, isModalConfirmed.PUBLIC]);

  const handlePostUntilChange = (postUntil: string) => {
    const filteredErrors = omit(errors, "post_until");
    setErrors({ ...filteredErrors });
    setPostUntil(postUntil);
  };

  const checkChanged = () => {
    if (isNew) {
      return false;
    }
    const currentData = formData();
    const originalData = pick(
      {
        ...activity,
        custom_fields: getPayloadCustomFields(activity.custom_fields),
        attachment_ids: activityAttachments.map(({ id }) => id)
      },
      Object.keys(currentData)
    );
    return !isEqual(currentData, originalData);
  };

  const isChanged = useMemo(
    () => checkChanged(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      name,
      description,
      postUntil,
      metadata,
      customFields,
      assignee,
      selectedParentActivity,
      minRequiredCount,
      attachments,
      audiences,
      communicationType
    ]
  );

  useEffect(() => {
    setCustomFields(uniqBy([...customFields, ...activity.custom_fields], (field) => field.type.id));
  }, [activity]);

  useEffect(() => {
    if (
      !isForceVisibilitySet &&
      selectedParentActivity &&
      template.default_visibility === "inherit_from_parent"
    ) {
      setIsPrivate(selectedParentActivity.private);
    }
  }, [selectedParentActivity]);

  useEffect(() => {
    if (
      isModalConfirmed[ModalKey.NoRequirements] ||
      isModalConfirmed[ModalKey.Misalignment] ||
      isModalConfirmed[ModalKey.QuestionsChanged]
    ) {
      handleSubmit(submitOptions);
      setSubmitOptions({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalConfirmed.NO_REQUIREMENTS, isModalConfirmed.MISALIGNMENT, isModalConfirmed.QUESTIONS_CHANGED]);

  // TODO: [PROF-1598] - this should be the initial value in assignee,
  // but this requires a cleanup of the logic in this component
  useEffect(() => {
    if (isNew && !assignee && doAutofillAssignee) {
      setAssignee(currentProfile);
    }
  }, [assignee, currentProfile, doAutofillAssignee, isNew]);

  return {
    name,
    setName,
    description,
    setDescription,
    customFields,
    setCustomFields,
    postUntil,
    setPostUntil,
    skillsFramework,
    setSkillsFramework,
    selectedParentActivity,
    setSelectedParentActivity,
    assignee,
    setAssignee,
    attachments,
    setAttachments,
    metadata,
    setMetadata,
    postUntilChangeReason,
    setPostUntilChangeReason,
    submitOptions,
    setSubmitOptions,
    minRequiredCount,
    setMinRequiredCount,
    isPrivate,
    setIsPrivate,
    audiences,
    setAudiences,
    communicationType,
    setCommunicationType,
    handleUploadRemove,
    questionsToggleValue,
    setQuestionsToggleValue,
    formData,
    handleSubmit,
    questionsRef,
    showQuestionsTile,
    subtemplateOptions,
    hasSubtemplates,
    subtemplate,
    subtemplateKey,
    isLoading,
    handleSubtemplateChange,
    errors,
    setErrors,
    removeErrorIfPresent,
    renderGrowlError,
    locked,
    isModalDisplayed,
    isModalConfirmed,
    setModalDisplayed,
    setModalConfirmed,
    engagementWizardState,
    handleEngagementWizardNavigation,
    handleQuestionChange,
    handleQuestionsToggleChange,
    handlePrivateChange,
    handlePostUntilChange,
    isChanged,
    showSkillsFrameworkButton,
    bookingCategory,
    setBookingCategory
  };
};
